import React from 'react';
import styled from 'styled-components';
import round from 'lodash/round';

import EMOJI_MAP from 'constants/emojis';
import { toNumber } from 'utils/number';
import theme from 'styles/theme';

import Columns from 'components/Columns';
import Currency from 'components/Currency';
import DebtBlock from './DebtBlock';

const StyledDebt = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  flex: 1 1 auto;

  .Debt__emoji {
    display: inline-flex;
    font-size: 32px;
  }

  .Debt__actions-container {
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 auto;

    ${props =>
      props.isActionable &&
      `
      &:hover {
        .Debt__blocks {
          top: 40px;
        }
      }
    `};
  }

  .Debt__actions {
    position: absolute;
    display: flex;
    align-items: center;
    left: 0;
    right: 0;
    top: 0;
    height: 40px;
    text-align: center;
  }

  .Debt__blocks {
    position: relative;
    top: 0;
    display: flex;
    flex: 1 1 auto;
    flex-flow: column;
    z-index: 1;
    transition: top 300ms;
  }

  .Debt__details {
    padding: 10px;
    text-align: center;
  }

  .Debt__name {
    white-space: nowrap;
    margin: 0 0 5px;
  }

  .Debt__number {
    white-space: nowrap;
    font-size: 12px;
    margin: 0 0 3px;
  }
`;

const Action = styled.div`
  display: inline-flex;
  font-weight: 500;
  font-size: 10px;
  cursor: pointer;
  padding: 10px 15px;
  text-transform: uppercase;
  transition: color 300ms;

  &:hover {
    color: ${props => (props.hover ? props.hover : `inherit`)};
  }
`;

const getSteps = (starting, amount = 500) => {
  if (starting <= 0) return [];

  const remainder = starting % amount;
  const cleanStart = starting - remainder;
  const totalSteps = Math.floor(cleanStart / amount) + 1;
  const arr = [...Array(totalSteps)].map((_, i) => cleanStart - i * amount);

  const result = remainder > 0 ? [starting, ...arr] : arr;
  return result.reverse();
};

const getEqualSteps = (starting, steps) => {
  const stepAmount = Math.ceil(starting / steps);
  return getSteps(starting, stepAmount);
};

const getDollarSteps = (starting, dollarAmount) => {
  return getSteps(starting, dollarAmount);
};

const Debt = ({ allowActions, color, incrementType, steps, onBalanceAction, ...debt }) => {
  const starting = toNumber(debt.starting, true);
  const blocks = incrementType === 'blockCount' ? getEqualSteps(starting, steps) : getDollarSteps(starting, debt.dollarSpecificity);

  return (
    <StyledDebt isActionable={allowActions}>
      <div className="Debt__actions-container">
        <Columns className="Debt__actions" fit spaceBetween middle span="2" gap="15">
          <Action hover={theme.red} onClick={() => onBalanceAction('interest', debt)}>
            Add Interest
          </Action>
          <Action hover={theme.green} onClick={() => onBalanceAction('payment', debt)}>
            Add Payment
          </Action>
        </Columns>
        <div className="Debt__blocks">
          {blocks.map((block, i) => (
            <DebtBlock color={color} complete={block >= debt.current} celebrate={block === 0} key={i}>
              {block > 0 ? (
                <Currency quantity={block} pattern="!##,### " />
              ) : (
                <span role="img" aria-label="Tada!">
                  🎉
                </span>
              )}
            </DebtBlock>
          ))}
          <DebtBlock color={color} celebrate complete>
            <span role="img" aria-label="Sadness">
              😭
            </span>
          </DebtBlock>
        </div>
      </div>
      <div className="Debt__details">
        <div className="Debt__emoji">
          <span role="img" aria-label={debt.type}>
            {EMOJI_MAP[debt.type]}
          </span>
        </div>
        <div className="Debt__name">{debt.name}</div>
        <div className="Debt__number">
          <strong>Balance:</strong> <Currency quantity={debt.current} />
        </div>
        <div className="Debt__number">
          <strong>Highest:</strong> <Currency quantity={debt.starting} />
        </div>
        <div className="Debt__number">
          <strong>Interest Rate:</strong> {debt.interest !== undefined ? `${debt.interest}%` : 'N/A'}
        </div>
        <div className="Debt__number">
          <strong>Progress:</strong> {`${round((debt.starting - debt.current) / debt.starting * 100, 2)}%`}
        </div>
      </div>
    </StyledDebt>
  );
};

export default Debt;



// WEBPACK FOOTER //
// ./src/components/DebtRainbow/DebtColumn.js